/**
 * TaxSetu AI - Authentication Context
 * ====================================
 * Manages user authentication state, Google OAuth, and query limits.
 * Implements conversion funnel: Guest (2) -> Guest Bypass (5 more) -> Login -> Beta Trial (2) -> Paywall
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from "../utils/api";

const API_URL = API_BASE_URL;

// App version for cache busting - Update this to force hard refresh for all users
const APP_VERSION = '4.0.1'; // Finance Act, 2026 & IT Rules 2026 (Notified) update

// Auth Context
const AuthContext = createContext(null);

// Funnel configuration
const FUNNEL_CONFIG = {
  guest_initial: 1,      // 1 free query before login wall
  guest_bypass: 0,       // No bypass - must login
  beta_trial: 50,        // 50 queries after login (Beta Trial)
};

// Version check and hard refresh
const checkAppVersion = () => {
  const storedVersion = localStorage.getItem('taxsetu_app_version');
  if (storedVersion !== APP_VERSION) {
    console.log(`App version changed: ${storedVersion} -> ${APP_VERSION}. Forcing refresh...`);
    localStorage.setItem('taxsetu_app_version', APP_VERSION);
    // Clear cache and force reload
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    window.location.reload(true);
    return true;
  }
  return false;
};

// Run version check immediately
checkAppVersion();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Funnel state
  const [funnelState, setFunnelState] = useState(() => {
    const saved = localStorage.getItem('taxsetu_funnel_state');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return {
          technicalQueries: 0,
          guestBypassUsed: false,
          betaTrialUsed: 0,
          stage: 'guest_initial' // guest_initial -> guest_bypass -> login_required -> beta_trial -> paywall
        };
      }
    }
    return {
      technicalQueries: 0,
      guestBypassUsed: false,
      betaTrialUsed: 0,
      stage: 'guest_initial'
    };
  });

  // Save funnel state to localStorage
  useEffect(() => {
    localStorage.setItem('taxsetu_funnel_state', JSON.stringify(funnelState));
  }, [funnelState]);

  // Load user from localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem('taxsetu_token');
    const savedUser = localStorage.getItem('taxsetu_user');
    
    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        // Update funnel stage for logged-in user
        if (funnelState.stage === 'guest_initial' || funnelState.stage === 'guest_bypass' || funnelState.stage === 'login_required') {
          setFunnelState(prev => ({
            ...prev,
            stage: 'beta_trial',
            betaTrialUsed: prev.betaTrialUsed || 0
          }));
        }
        verifyToken(token);
      } catch (e) {
        console.error('Failed to parse saved user:', e);
        logout();
      }
    }
    
    setLoading(false);
  }, []);

  // Verify token with backend
  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        localStorage.setItem('taxsetu_user', JSON.stringify(userData));
      } else {
        logout();
      }
    } catch (error) {
      console.error('Token verification failed:', error);
    }
  };

  // Google OAuth Login
  const loginWithGoogle = useCallback(async () => {
    try {
      const redirectUri = `${window.location.origin}/auth/google`;
      const response = await fetch(`${API_URL}/api/auth/google/url?redirect_uri=${encodeURIComponent(redirectUri)}`);
      const data = await response.json();
      
      if (data.auth_url) {
        localStorage.setItem('taxsetu_oauth_state', data.state);
        window.location.href = data.auth_url;
      }
    } catch (error) {
      console.error('Failed to initiate Google OAuth:', error);
      throw error;
    }
  }, []);

  // Google One-Tap Sign-In callback
  const handleOneTapCallback = useCallback(async (credentialResponse) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/google/one-tap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: credentialResponse.credential })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Authentication failed');
      }
      
      const data = await response.json();
      
      localStorage.setItem('taxsetu_token', data.access_token);
      localStorage.setItem('taxsetu_user', JSON.stringify(data.user));
      setUser(data.user);
      
      // Move to beta_trial stage
      setFunnelState(prev => ({
        ...prev,
        stage: 'beta_trial',
        betaTrialUsed: 0
      }));
      
      // Reload to reflect login state
      window.location.reload();
      
      return data.user;
    } catch (error) {
      console.error('One-Tap callback failed:', error);
      throw error;
    }
  }, []);

  // Initialize Google One-Tap
  const initializeOneTap = useCallback(async () => {
    // Don't show One-Tap if already logged in
    if (user) return;
    
    try {
      // Get client ID from backend
      const response = await fetch(`${API_URL}/api/auth/google/client-id`);
      const data = await response.json();
      
      if (!data.client_id || !window.google) return;
      
      window.google.accounts.id.initialize({
        client_id: data.client_id,
        callback: handleOneTapCallback,
        auto_select: true, // Auto-select for returning users
        cancel_on_tap_outside: false,
        context: 'signin',
        ux_mode: 'popup',
        itp_support: true,
        use_fedcm_for_prompt: true // FedCM migration - required by Google
      });
      
      // Display the One-Tap prompt with FedCM-compatible callback
      window.google.accounts.id.prompt((notification) => {
        // FedCM migration: These methods may return undefined with FedCM
        // Use optional chaining to prevent errors
        const notDisplayedReason = notification.getNotDisplayedReason?.();
        const skippedReason = notification.getSkippedReason?.();
        const dismissedReason = notification.getDismissedReason?.();
        
        if (notDisplayedReason) {
          console.log('One-Tap not displayed:', notDisplayedReason);
        } else if (skippedReason) {
          console.log('One-Tap skipped:', skippedReason);
        } else if (dismissedReason) {
          console.log('One-Tap dismissed:', dismissedReason);
        }
      });
    } catch (error) {
      console.error('Failed to initialize One-Tap:', error);
    }
  }, [user, handleOneTapCallback]);

  // Handle Google OAuth callback
  const handleGoogleCallback = useCallback(async (code) => {
    try {
      const redirectUri = `${window.location.origin}/auth/google`;
      const response = await fetch(`${API_URL}/api/auth/google/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, redirect_uri: redirectUri })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Authentication failed');
      }
      
      const data = await response.json();
      
      localStorage.setItem('taxsetu_token', data.access_token);
      localStorage.setItem('taxsetu_user', JSON.stringify(data.user));
      setUser(data.user);
      
      // Move to beta_trial stage
      setFunnelState(prev => ({
        ...prev,
        stage: 'beta_trial',
        betaTrialUsed: 0
      }));
      
      return data.user;
    } catch (error) {
      console.error('Google callback failed:', error);
      throw error;
    }
  }, []);

  // Mobile login with Twilio OTP
  const sendOTP = useCallback(async (phoneNumber, channel = 'whatsapp') => {
    try {
      const response = await fetch(`${API_URL}/api/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          phone_number: phoneNumber,
          channel: channel
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send OTP');
      }
      
      return data;
    } catch (error) {
      console.error('Send OTP failed:', error);
      throw error;
    }
  }, []);

  const verifyOTP = useCallback(async (phoneNumber, code) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          phone_number: phoneNumber,
          code: code
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Verification failed');
      }
      
      if (data.valid && data.user && data.token) {
        // Store token and user
        localStorage.setItem('taxsetu_token', data.token);
        localStorage.setItem('taxsetu_user', JSON.stringify(data.user));
        setUser(data.user);
        
        // Move to beta_trial stage (logged in user)
        setFunnelState(prev => ({
          ...prev,
          stage: 'beta_trial',
          betaTrialUsed: 0
        }));
        
        return data;
      } else {
        throw new Error(data.message || 'Invalid OTP');
      }
    } catch (error) {
      console.error('Verify OTP failed:', error);
      throw error;
    }
  }, []);

  // Legacy mobile login (for backwards compatibility)
  const loginWithMobile = useCallback(async (phoneNumber) => {
    // Now handled by sendOTP + verifyOTP flow
    console.log('Use sendOTP and verifyOTP instead');
    return null;
  }, []);

  // Logout
  const logout = useCallback(() => {
    localStorage.removeItem('taxsetu_token');
    localStorage.removeItem('taxsetu_user');
    setUser(null);
    // Don't reset funnel state - user might return
  }, []);

  // Check if query is technical (tax-related)
  const isTechnicalQuery = useCallback((queryText) => {
    const generalPatterns = [
      /^(hi|hello|hey|namaste|thanks|thank you|ok|okay|bye|goodbye|good morning|good evening|good night|dhanyawad|shukriya)[\s!.,?]*$/i,
      /^(haan|nahi|yes|no|ji|theek hai|accha|okay)[\s!.,?]*$/i,
      /^(who are you|what is your name|tumhara naam|aap kaun)[\s!?]*$/i
    ];
    
    const lowerQuery = queryText.toLowerCase().trim();
    
    for (const pattern of generalPatterns) {
      if (pattern.test(lowerQuery)) {
        return false; // General query
      }
    }
    
    return true; // Technical query
  }, []);

  // Check query limit - returns action needed
  const checkQueryLimit = useCallback(async (queryType, queryText) => {
    // General queries always allowed
    if (queryType === 'general' || !isTechnicalQuery(queryText)) {
      return { allowed: true, type: 'general' };
    }
    
    const { stage, technicalQueries, guestBypassUsed, betaTrialUsed } = funnelState;
    
    // Pro users - unlimited
    if (user?.tier === 'pro' || user?.subscription === 'pro') {
      return { allowed: true, type: 'pro' };
    }
    
    // Logged in users - unlimited for now (beta)
    if (user) {
      return { allowed: true, type: 'logged_in' };
    }
    
    // Guest users - max 2 queries then force login
    if (technicalQueries >= FUNNEL_CONFIG.guest_initial) {
      return { 
        allowed: false, 
        action: 'login_wall',
        queriesUsed: technicalQueries,
        maxQueries: FUNNEL_CONFIG.guest_initial
      };
    }
    
    return { allowed: true, type: 'guest' };
  }, [user, funnelState, isTechnicalQuery]);

  // Continue as guest (bypass)
  const continueAsGuest = useCallback(() => {
    setFunnelState(prev => ({
      ...prev,
      guestBypassUsed: true,
      stage: 'guest_bypass'
    }));
  }, []);

  // Increment query count
  const incrementQueryCount = useCallback(async (queryType, queryText, responseText, sessionId) => {
    const token = localStorage.getItem('taxsetu_token');
    const isTechnical = isTechnicalQuery(queryText);
    
    // Log query for analytics
    try {
      await fetch(`${API_URL}/api/auth/log-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          query_type: isTechnical ? 'technical' : 'general',
          query_text: queryText,
          response_text: responseText,
          session_id: sessionId,
          funnel_stage: funnelState.stage
        })
      });
    } catch (error) {
      console.error('Failed to log query:', error);
    }
    
    // Only count technical queries
    if (!isTechnical) return;
    
    setFunnelState(prev => {
      const newState = { ...prev };
      
      if (prev.stage === 'beta_trial' && user) {
        newState.betaTrialUsed = (prev.betaTrialUsed || 0) + 1;
        if (newState.betaTrialUsed >= FUNNEL_CONFIG.beta_trial) {
          newState.stage = 'paywall';
        }
      } else if (!user) {
        newState.technicalQueries = (prev.technicalQueries || 0) + 1;
      }
      
      return newState;
    });
  }, [user, funnelState.stage, isTechnicalQuery]);

  // Get current usage info
  const getUsageInfo = useCallback(() => {
    const { stage, technicalQueries, betaTrialUsed } = funnelState;
    
    if (user?.tier === 'pro' || user?.subscription === 'pro') {
      return {
        tier: 'pro',
        tierName: 'Pro',
        used: 0,
        limit: Infinity,
        remaining: Infinity,
        isUnlimited: true,
        stage: 'pro'
      };
    }
    
    if (user) {
      return {
        tier: 'registered',
        tierName: 'Beta Trial',
        used: betaTrialUsed,
        limit: FUNNEL_CONFIG.beta_trial,
        remaining: Math.max(0, FUNNEL_CONFIG.beta_trial - betaTrialUsed),
        isUnlimited: false,
        stage
      };
    }
    
    const maxGuest = stage === 'guest_bypass' 
      ? FUNNEL_CONFIG.guest_initial + FUNNEL_CONFIG.guest_bypass 
      : FUNNEL_CONFIG.guest_initial;
    
    return {
      tier: 'guest',
      tierName: stage === 'guest_bypass' ? 'Guest (Extended)' : 'Guest',
      used: technicalQueries,
      limit: maxGuest,
      remaining: Math.max(0, maxGuest - technicalQueries),
      isUnlimited: false,
      stage
    };
  }, [user, funnelState]);

  // Reset funnel for testing
  const resetFunnel = useCallback(() => {
    setFunnelState({
      technicalQueries: 0,
      guestBypassUsed: false,
      betaTrialUsed: 0,
      stage: 'guest_initial'
    });
    localStorage.removeItem('taxsetu_funnel_state');
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
    loginWithGoogle,
    loginWithMobile,
    sendOTP,
    verifyOTP,
    handleGoogleCallback,
    handleOneTapCallback,
    initializeOneTap,
    logout,
    checkQueryLimit,
    incrementQueryCount,
    getUsageInfo,
    continueAsGuest,
    resetFunnel,
    funnelState,
    isTechnicalQuery,
    getToken: () => localStorage.getItem('taxsetu_token')
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
